import { useRef, useEffect } from "react";
import {
  TextB, TextItalic, TextUnderline, TextHOne, TextHTwo, ListBullets, ListNumbers,
  TextAlignLeft, TextAlignCenter, TextAlignRight, Minus, Link as LinkIcon, CursorClick, BracketsCurly,
} from "@phosphor-icons/react";

// Lightweight, no-HTML-knowledge rich text editor (contentEditable). Emits sanitized-ish HTML;
// the backend re-sanitizes with an allowlist before storing/sending.
export function RichTextEditor({ value, onChange, variables = [], testid = "rte" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) ref.current.innerHTML = value || "";
  }, [value]);

  const emit = () => onChange?.(ref.current?.innerHTML || "");
  const cmd = (c, v = null) => { document.execCommand(c, false, v); ref.current?.focus(); emit(); };

  const insertHtml = (html) => {
    ref.current?.focus();
    document.execCommand("insertHTML", false, html);
    emit();
  };
  const insertVariable = (key) => insertHtml(`{{${key}}}`);
  const insertButton = () => {
    const label = window.prompt("Button label", "Pay Now");
    if (label === null) return;
    const url = window.prompt("Button link (URL or a variable like {{payment_link}})", "{{payment_link}}") || "#";
    insertHtml(`<a href="${url}" data-cta="1" style="display:inline-block;background:#0B3D91;color:#ffffff;padding:10px 18px;border-radius:6px;text-decoration:none;font-weight:bold;">${label}</a>&nbsp;`);
  };
  const insertLink = () => {
    const url = window.prompt("Link URL", "https://");
    if (url) cmd("createLink", url);
  };

  const Tool = ({ Icon, title, onClick, tid }) => (
    <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      data-testid={`${testid}-${tid}`}
      className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-secondary text-slate-700">
      <Icon size={16} weight="bold" />
    </button>
  );

  return (
    <div className="border border-border rounded-sm" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1 bg-secondary/40">
        <Tool Icon={TextHOne} title="Heading" tid="h1" onClick={() => cmd("formatBlock", "<h2>")} />
        <Tool Icon={TextHTwo} title="Subheading" tid="h2" onClick={() => cmd("formatBlock", "<h3>")} />
        <span className="w-px h-5 bg-border mx-1" />
        <Tool Icon={TextB} title="Bold" tid="bold" onClick={() => cmd("bold")} />
        <Tool Icon={TextItalic} title="Italic" tid="italic" onClick={() => cmd("italic")} />
        <Tool Icon={TextUnderline} title="Underline" tid="underline" onClick={() => cmd("underline")} />
        <span className="w-px h-5 bg-border mx-1" />
        <Tool Icon={TextAlignLeft} title="Align left" tid="left" onClick={() => cmd("justifyLeft")} />
        <Tool Icon={TextAlignCenter} title="Align center" tid="center" onClick={() => cmd("justifyCenter")} />
        <Tool Icon={TextAlignRight} title="Align right" tid="right" onClick={() => cmd("justifyRight")} />
        <span className="w-px h-5 bg-border mx-1" />
        <Tool Icon={ListBullets} title="Bulleted list" tid="ul" onClick={() => cmd("insertUnorderedList")} />
        <Tool Icon={ListNumbers} title="Numbered list" tid="ol" onClick={() => cmd("insertOrderedList")} />
        <Tool Icon={Minus} title="Divider" tid="hr" onClick={() => insertHtml("<hr/>")} />
        <Tool Icon={LinkIcon} title="Link" tid="link" onClick={insertLink} />
        <Tool Icon={CursorClick} title="CTA button" tid="button" onClick={insertButton} />
        <div className="ml-auto relative group">
          <button type="button" onMouseDown={(e) => e.preventDefault()}
            className="flex items-center gap-1 h-8 px-2 rounded-sm hover:bg-secondary text-xs font-semibold text-slate-700"
            data-testid={`${testid}-insert-variable`}>
            <BracketsCurly size={15} weight="bold" /> Insert Variable
          </button>
          <div className="absolute right-0 top-full z-20 hidden group-hover:block w-56 max-h-64 overflow-y-auto border border-border rounded-sm bg-white shadow-lg py-1">
            {variables.map((v) => (
              <button key={v.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertVariable(v.key)}
                data-testid={`${testid}-var-${v.key}`}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-secondary flex items-center justify-between gap-2">
                <span>{v.label}</span><span className="mono text-[10px] text-muted-foreground">{`{{${v.key}}}`}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={emit} onBlur={emit}
        data-testid={`${testid}-body`}
        className="min-h-[220px] p-3 text-sm leading-relaxed focus:outline-none prose-sm max-w-none" />
    </div>
  );
}
